import { PageInfo } from '@/@types/common';
import { DEFAULT_PAGINATION_LIMIT } from '@/constants/config';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import { isNotEmpty } from '@/utils';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { ColumnProps, Pagination, Table as RSTable, TableProps as RSTableProps } from 'rsuite5';
import usePrefix from '@/hooks/usePrefix';
import { TableInstance } from 'rsuite5/esm/Table';
import PageLoader from '@/components/shared/PageLoader';
import { PaginationGroupProps } from 'rsuite5/esm/Pagination/PaginationGroup';
import { Button } from '..';
export const { Column, HeaderCell, Cell } = RSTable;

// column 宽度数值
export const WIDTH_MAP = {
  sm: 100,
  md: 150,
  lg: 200,
  xl: 250,
  xxl: 300,
};

type ValueType = string | number;

export interface CustomColumnProps extends Omit<ColumnProps, 'width' | 'minWidth'> {
  width?: number | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  minWidth?: number | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
}

export interface ColumnConfig {
  /** 表头文本 */
  header?: React.ReactNode;
  cell?: React.ReactNode;
  columnProps?: CustomColumnProps;
}

/** 只用一行row显示的table数据属性 */
type OneRowDataType = {
  data: any[];
  /** 当前分页 */
  page: number;
  /** table里所有展示内容 */
  content: React.ReactNode;
};

export interface TableCommonProps<T> {
  wrapperClassNames?: string;
  /** 单行显示的全部数据 */
  oneRowData?: OneRowDataType;
  onChange?: (params: Record<string, any>) => void;
  /** 分页信息 */
  pageInfo?: PageInfo;
  /** columns 配置 */
  columns?: ColumnConfig[];
}

export type TableProps = TableCommonProps<ValueType> & Partial<RSTableProps>;

interface TableContextValue {
  loading?: boolean;
  data?: any;
  oneRowData?: OneRowDataType;
  pageInfo?: PageInfo;
  columns?: ColumnConfig[];
  mergeParams?: (params: Record<string, any>) => any;
  onChange?: (params: Record<string, any>) => void;
}

const TableContext = React.createContext<TableContextValue>(null);

const BicesterTable = ({
  style,
  data,
  oneRowData,
  loading,
  wrapperClassNames,
  className,
  pageInfo,
  columns = [],
  onChange,
  children,
  ...props
}: TableProps) => {
  const { prefix, classname } = usePrefix('bicester-table');

  const mergeParams = (params) => {
    return {
      page: pageInfo?.page,
      ...params,
    };
  };

  return (
    <TableContext.Provider
      value={{
        loading,
        data: oneRowData && oneRowData?.data?.length ? [{}] : data,
        oneRowData,
        pageInfo: oneRowData ? { page: oneRowData.page, total: null } : pageInfo,
        columns: oneRowData
          ? [
              {
                columnProps: { flexGrow: 1 },
                cell: <Cell>{() => oneRowData?.content}</Cell>,
              },
            ]
          : columns,
        mergeParams,
        onChange,
      }}
    >
      <div className={classname} style={style}>
        {children}
      </div>
    </TableContext.Provider>
  );
};

const BicesterTableBody = ({
  height = LAYOUT_CONFIG.TABLE_MIN_HEIGHT,
  headerHeight = LAYOUT_CONFIG.TABLE_HEADER_HEIGHT,
  rowHeight = LAYOUT_CONFIG.TABLE_ROW_HEIGHT,
  className,
  children,
  style,
  isEmptyHoverBody = true, // 空数据展示元素覆盖body，包括表头
  bodyEmpty, // 覆盖body的空数据展示元素
  sortColumn,
  sortType,
  onSortColumn,
  ...props
}: {
  height?: number;
  rowHeight?: number | ((rowData) => number);
  className?: number;
  style?: any;
  children?: React.ReactNode;
  isEmptyHoverBody?: boolean;
  bodyEmpty?: React.ReactNode;
  sortColumn?: string;
  sortType?: "desc" | "asc";
  onSortColumn?: (column: string, sortType: "desc" | "asc") => void;
  [rest: string]: any;
}) => {
  const { prefix, classname } = usePrefix('bicester-table-body');
  const { loading, data, oneRowData, columns, pageInfo, mergeParams, onChange } = React.useContext(TableContext);
  const tableRef = useRef<TableInstance>(null);

  useEffect(() => {
    if (_.isNumber(pageInfo?.page)) {
      // 表格翻页时返回顶部
      tableRef.current?.scrollTop(0);
    }
  }, [pageInfo?.page]);

  const getColumnProps = (props: CustomColumnProps = {}): ColumnProps => {
    const { width, minWidth } = props;
    const columnPropsWithDefaultValue = {
      resizable: false,
      sortable: false,
      ...props,
    };
    return Object.assign(
      {},
      columnPropsWithDefaultValue,
      WIDTH_MAP[width] && { width: WIDTH_MAP[width] },
      WIDTH_MAP[minWidth] && { minWidth: WIDTH_MAP[minWidth] }
    );
  };

  const renderColumns = () => {
    const tableColumns = columns.map(({ cell, header, columnProps }, index) => {
      // 把column属性值转换成原生rsuite table组件支持的column属性值
      const convertedColProps = getColumnProps(columnProps);

      return (
        <Column key={index} {...convertedColProps}>
          <HeaderCell>{header}</HeaderCell>
          {cell}
        </Column>
      );
    });
    return tableColumns;
  };

  const handleSortColumn = (sortColumn: string, sortType: 'asc' | 'desc') => {
    /**
     * 切换排序时，需要重置页数为1
     */
    onChange?.(mergeParams({ page: 1, order_field: sortColumn, order_sort: sortType }));
    onSortColumn(sortColumn, sortType);
  }

  const renderChildren = () => {
    return React.Children.toArray(children).filter(isNotEmpty);
  };

  const tableLocale = {
    emptyMessage: '',
    loading: '加载中',
  };

  const shouldRenderColumn = columns.length > 0;

  return (
    <div style={{ width: '100%', height, position: 'relative', ...style }}>
      {isEmptyHoverBody && !data?.length ? (
        <>
          {/* 全局PageLoader的z-index是99 */}
          {loading && <PageLoader style={{ position: 'absolute', zIndex: 98 }} />}
          <div
            className="w-full h-full flex justify-center items-center text-[#B9B9B9] text-[20px]"
            style={{ height }}
          >
            {bodyEmpty || '暂无记录'}
          </div>
        </>
      ) : (
        <RSTable
          ref={tableRef}
          height={height}
          hover={false}
          {...props}
          className={`${classname}${oneRowData ? ' only-one-row' : ''} ${className || ''}`}
          data={data}
          loading={loading}
          renderEmpty={() => (
            <div className="w-full h-full flex justify-center items-center text-[#B9B9B9] text-[20px]">
              {bodyEmpty || '暂无记录'}
            </div>
          )}
          headerHeight={headerHeight}
          rowHeight={rowHeight}
          locale={tableLocale}
          sortType={sortType}
          sortColumn={sortColumn}
          onSortColumn={handleSortColumn}
        >
          {shouldRenderColumn ? renderColumns() : renderChildren()}
        </RSTable>
      )}
    </div>
  );
};

const BicesterTablePagination = ({ style, ...props }: Omit<PaginationGroupProps, 'total'>) => {
  const { prefix, classname } = usePrefix('bicester-table-pagination');
  const { pageInfo, mergeParams, onChange } = React.useContext(TableContext);

  // 翻页
  const handleChangePage = (page: number) => {
    onChange?.(mergeParams({ page }));
  };

  return (
    <Pagination
      className={classname}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: LAYOUT_CONFIG.TABLE_PAGINATION_MARGINTOP_HEIGHT,
        ...style,
      }}
      activePage={_.isNumber(pageInfo?.page) ? pageInfo?.page : Number(pageInfo?.page ?? 1)}
      total={pageInfo?.total || 1}
      limit={DEFAULT_PAGINATION_LIMIT}
      onChangePage={handleChangePage}
      first={false}
      last={false}
      prev
      next
      maxButtons={3}
      {...props}
    />
  );
};

/**
 * 中文的分页器，total已知(设计稿上去掉了，可废弃)
 */
const BicesterTablePaginationZh = (props) => {
  const { prefix, classname } = usePrefix('bicester-table-paginationZh');
  const { pageInfo, mergeParams, onChange } = React.useContext(TableContext);

  const handleChangePage = (page: number) => {
    onChange?.(mergeParams({ page }));
  };

  return (
    <Pagination
      className={classname}
      activePage={pageInfo?.page || 1}
      total={pageInfo?.total || 0}
      onChangePage={handleChangePage}
      first={false}
      last={false}
      prev
      next
      {...props}
    />
  );
};

/**
 * 中文的分页器，total未知
 */
const BicesterTablePaginationZhNoTotal = (props) => {
  const { prefix, classname } = usePrefix('bicester-table-paginationZh-no-total');
  const { oneRowData, pageInfo, mergeParams, onChange } = React.useContext(TableContext);

  const handleChangePage = (page: number) => {
    onChange?.(mergeParams({ page }));
  };

  return (
    <div className={classname}>
      <Button
        className="mr-[20px]"
        appearance="subtle"
        title="上一页"
        disabled={pageInfo?.page === 1}
        onClick={() => handleChangePage(pageInfo?.page - 1)}
      >
        上一页
      </Button>
      <Button
        appearance="subtle"
        title="下一页"
        disabled={!oneRowData?.data?.length}
        onClick={() => handleChangePage(pageInfo?.page + 1)}
      >
        下一页
      </Button>
    </div>
  );
};

const BicesterTableTotal = () => {
  const { prefix, classname } = usePrefix('bicester-table-total');
  const { pageInfo } = React.useContext(TableContext);

  return <div className={classname}>总计 {pageInfo?.total || 0} 条</div>;
};

BicesterTable.Body = BicesterTableBody;
BicesterTable.Pagination = BicesterTablePagination;
BicesterTable.PaginationZh = BicesterTablePaginationZh;
BicesterTable.PaginationZhNoTotal = BicesterTablePaginationZhNoTotal;
BicesterTable.Total = BicesterTableTotal;

export default BicesterTable;
